const FAQS = [
  {
    q: 'How do I create an account?',
    a: 'Download the Hadafi app and tap "Sign Up." Enter your name, email, date of birth, and a password. You can then build your player profile by adding your position, preferred foot, and a profile photo.',
  },
  {
    q: 'How do I find or join a game?',
    a: 'From the home screen, tap "Find a Game." Enable location permissions so Hadafi can show games near you. Browse available games, tap one to see details, and hit "Join" to request a spot.',
  },
  {
    q: 'How do I create a game?',
    a: 'Tap the "+" button on the home screen and select "Create Game." Fill in the location, date, time, number of players, and any additional notes. Your game will be visible to nearby players once published.',
  },
  {
    q: 'How do I delete my account?',
    a: 'Go to your Profile → Settings → Account → Delete Account. Your data will be permanently removed within 30 days. If you need help, email us at support@hadafi.ae.',
  },
  {
    q: 'Why does Hadafi need my location?',
    a: 'Location is used to show you games and players near you. You can disable location access in your device settings at any time, though some features will be limited without it.',
  },
  {
    q: 'How do I report a player or inappropriate content?',
    a: 'Tap the three-dot menu on any profile or post and select "Report." Describe the issue and submit. Our moderation team reviews all reports within 48 hours.',
  },
  {
    q: 'I forgot my password. How do I reset it?',
    a: 'On the login screen, tap "Forgot Password?" and enter your registered email address. You will receive a reset link within a few minutes. Check your spam folder if you do not see it.',
  },
  {
    q: 'The app is crashing or not loading. What should I do?',
    a: 'Try force-closing the app and reopening it. If the issue persists, restart your device or check for a pending app update in the App Store. You can also reach us at support@hadafi.ae with your device model and iOS version.',
  },
];

export default function SupportPage() {
  return (
    <div style={{ paddingTop: 68 }}>

      {/* HEADER */}
      <section style={{
        padding: '5rem clamp(1rem,4vw,3rem) 3rem',
        background: 'var(--navy2)',
        borderBottom: '1px solid rgba(232,96,74,0.1)',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span className="section-tag">SUPPORT</span>
          <h1 className="section-title" style={{ fontSize: 'clamp(38px,6vw,62px)', marginBottom: '1rem' }}>
            How can we<br /><span>Help You?</span>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.7, maxWidth: 560 }}>
            Find answers to common questions below, or reach out to the Hadafi support team directly — we typically respond within one business day.
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section style={{ padding: '3rem clamp(1rem,4vw,3rem) 0' }}>
        <div style={{
          maxWidth: 800, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.25rem',
        }}>
          {[
            {
              icon: '✉️',
              title: 'Email Support',
              desc: 'General questions and account help',
              action: 'support@hadafi.ae',
              href: 'mailto:support@hadafi.ae',
            },
            {
              icon: '🛡️',
              title: 'Report a Problem',
              desc: 'Safety, abuse, or inappropriate content',
              action: 'report@hadafi.ae',
              href: 'mailto:report@hadafi.ae',
            },
            {
              icon: '🔒',
              title: 'Privacy Requests',
              desc: 'Data deletion or access requests',
              action: 'privacy@hadafi.ae',
              href: 'mailto:privacy@hadafi.ae',
            },
          ].map(({ icon, title, desc, action, href }) => (
            <a key={title} href={href} style={{
              display: 'block', textDecoration: 'none',
              padding: '1.5rem',
              background: 'var(--navy2)',
              border: '1px solid rgba(232,96,74,0.15)',
              borderRadius: 8,
              transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(232,96,74,0.5)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(232,96,74,0.15)'}
            >
              <div style={{ fontSize: 28, marginBottom: '0.75rem' }}>{icon}</div>
              <p style={{ fontFamily: 'var(--font-d)', fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: '0.35rem' }}>{title}</p>
              <p style={{ fontFamily: 'var(--font-m)', fontSize: 13, color: 'var(--muted)', marginBottom: '0.75rem' }}>{desc}</p>
              <p style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--coral)' }}>{action}</p>
            </a>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '3rem clamp(1rem,4vw,3rem) 5rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <p style={{ fontFamily: 'var(--font-m)', fontSize: 10, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: '0.5rem' }}>FAQ</p>
          <h2 style={{ fontFamily: 'var(--font-d)', fontSize: 28, fontWeight: 800, textTransform: 'uppercase', marginBottom: '2rem' }}>
            Frequently Asked <span style={{ color: 'var(--coral)' }}>Questions</span>
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {FAQS.map(({ q, a }) => (
              <details key={q} style={{
                background: 'var(--navy2)',
                border: '1px solid rgba(232,96,74,0.15)',
                borderRadius: 8,
                padding: '1.25rem 1.5rem',
              }}>
                <summary style={{
                  fontFamily: 'var(--font-d)', fontSize: 15, fontWeight: 700,
                  color: 'var(--white)', cursor: 'pointer', listStyle: 'none',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: '1rem',
                }}>
                  {q}
                  <span style={{ color: 'var(--coral)', fontSize: 18, flexShrink: 0 }}>+</span>
                </summary>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.8, marginTop: '0.75rem' }}>{a}</p>
              </details>
            ))}
          </div>

          {/* Still need help */}
          <div style={{
            marginTop: '3rem', padding: '2rem',
            background: 'rgba(232,96,74,0.07)',
            border: '1px solid rgba(232,96,74,0.2)',
            borderRadius: 8, textAlign: 'center',
          }}>
            <p style={{ fontFamily: 'var(--font-d)', fontSize: 20, fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Still Need Help?
            </p>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: '1.25rem' }}>
              Our support team is available Sunday – Thursday, 9 AM – 6 PM GST.
            </p>
            <a href="mailto:support@hadafi.ae" style={{
              display: 'inline-block',
              background: 'var(--coral)', color: '#fff',
              fontFamily: 'var(--font-d)', fontSize: 13, fontWeight: 700,
              letterSpacing: 1, textTransform: 'uppercase',
              padding: '0.75rem 2rem', borderRadius: 4,
              textDecoration: 'none',
            }}>
              Contact Support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
